import React from "react";
import Pillar3D from "./Pillar3D";
import { MEDAL_STYLES } from "./medalStyles";

/**
 * PodiumStep — single large step of the main podium (one per rank 1/2/3).
 *
 * In `efficiency` mode the headline number is tx/hr; in `volume` mode it is
 * the total transaction count.
 */
export default function PodiumStep({ rank, person, mode }) {
  if (!person) return <div className="flex-1" data-testid={`podium-step-${rank}-empty`} />;
  const style = MEDAL_STYLES[rank];
  const Icon = style.icon;
  const headline =
    mode === "efficiency" && person.tx_per_hour != null
      ? person.tx_per_hour.toFixed(2)
      : (person.total || 0).toLocaleString();
  const subline = mode === "efficiency" ? "tx/hr" : null;
  return (
    <div className="flex flex-1 flex-col items-center gap-3" data-testid={`podium-step-${rank}`}>
      {/* Name above the step — full contrast and wraps if long. */}
      <div className="min-h-[3rem] w-full px-1 text-center">
        <div
          className="break-words text-sm font-semibold leading-tight text-foreground"
          data-testid={`podium-name-${rank}`}
          title={person.employee_name}
        >
          {person.employee_name}
        </div>
        {person.employee_id && (
          <div className="font-mono text-[10px] text-muted-foreground">
            {person.employee_id}
          </div>
        )}
      </div>

      <Pillar3D
        style={style}
        depth={14}
        rounded={6}
        className={`w-full ${style.height} transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.03]`}
      >
        {/* Medal badge — sits on top of the 3D pillar. */}
        <div
          className="absolute left-1/2 top-0 z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-background shadow-md"
          style={{ background: style.front, boxShadow: style.glow }}
          data-testid={`podium-badge-${rank}`}
        >
          <Icon
            className="h-5 w-5 text-white"
            style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.5))" }}
          />
        </div>

        {/* Big number + sub label, centered & bottom-anchored within the pillar. */}
        <div className="absolute inset-x-0 bottom-3 flex flex-col items-center">
          <div
            className="text-center text-white"
            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.55)" }}
          >
            <div className="text-3xl font-bold leading-none tabular-nums">
              {headline}
            </div>
            {subline && (
              <div className="mt-1 text-[10px] font-medium uppercase tracking-wider opacity-90">
                {subline}
              </div>
            )}
            <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-90">
              {style.label}
            </div>
          </div>
        </div>
      </Pillar3D>
    </div>
  );
}
